
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/auth.service';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      const response = await authService.forgotPassword({ email });
      setMessage(response.message || 'If an account exists with this email, you will receive reset instructions.');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6 relative overflow-hidden">
      {/* Cinematic Background Glows */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-md w-full space-y-8 glass-panel p-10 lg:p-12 rounded-[2.5rem] border border-slate-800 shadow-2xl relative z-10 animate-in fade-in zoom-in duration-700">
        <div className="text-center">
          {/* New AICW Hex-Core Logo */}
          <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 mb-6 shadow-2xl shadow-purple-600/20">
            <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
              <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
              <circle cx="12" cy="13.5" r="1.2" fill="white" className="animate-pulse" />
              <path d="M8 18L12 16L16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-3xl font-black text-white tracking-tight uppercase">Reset Password</h2>
          <p className="mt-2 text-[10px] text-purple-400 font-bold uppercase tracking-[0.2em]">Secure Recovery Protocol</p>
        </div>

        {message && <div className="bg-green-500/10 border border-green-500/20 text-green-500 text-[11px] p-5 rounded-2xl text-center font-black uppercase tracking-widest leading-relaxed">{message}</div>}
        {error && <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-[11px] p-5 rounded-2xl text-center font-black uppercase tracking-widest">{error}</div>}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="group">
            <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Email Address</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none font-bold placeholder-slate-700 text-sm"
              placeholder="you@example.com"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 px-6 rounded-2xl text-white bg-purple-600 hover:bg-purple-500 font-black uppercase tracking-[0.2em] text-xs transition-all shadow-2xl shadow-purple-600/30 active:scale-95 disabled:opacity-50"
          >
            {isSubmitting ? 'Requesting Link...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="text-center pt-4 border-t border-white/5">
          <Link to="/login" className="text-[10px] font-black text-purple-500 hover:text-purple-400 uppercase tracking-widest transition-colors">
            Back to Secure Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
